import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";
import { Library, ListChecks, Award, Bookmark, MessageSquare, Tag, BookOpen } from "lucide-react";

const quickLinks = [
  { to: "/profil", label: "Kütüphanem", icon: Library },
  { to: "/mesajlar", label: "Mesajlarım", icon: MessageSquare },
  { to: "/listeler", label: "Okuma Listelerim", icon: ListChecks },
  { to: "/rozetler", label: "Rozetlerim", icon: Award },
  { to: "/kaydedilenler", label: "Kaydedilen Alıntılarım", icon: Bookmark },
];

export function LeftSidebar() {
  const { user } = useAuth();
  const [ap, setAp] = useState(null);
  const [stats, setStats] = useState(null);
  const [popTags, setPopTags] = useState([]);
  const [dailyBook, setDailyBook] = useState(null);

  useEffect(() => {
    api.get("/stats/me").then((r) => setAp(r.data)).catch(() => {});
    api.get("/profile/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/books/popular-tags").then((r) => setPopTags(r.data)).catch(() => {});
    api.get("/books/daily").then((r) => setDailyBook(r.data)).catch(() => {});
  }, []);

  return (
    <aside className="hidden xl:block space-y-4 xl:sticky xl:top-20 xl:self-start" data-testid="left-sidebar">
      <div className="border border-line rounded-xl p-4 bg-surface">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden shrink-0">
            {user?.profil_resmi ? <img src={user.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-xl text-terra">{user?.ad_soyad?.[0]}</span>}
          </div>
          <div className="min-w-0">
            <Link to="/profil" className="font-medium text-ink hover:text-terra text-sm truncate block">{user?.ad_soyad}</Link>
            {ap && <p className="text-xs text-muted-foreground">Seviye {ap.level}: {ap.level_name}</p>}
          </div>
        </div>
        {ap && (
          <div className="mt-3">
            <div className="flex justify-between text-[11px] text-muted-foreground"><span>{ap.total_ap} AP</span><span>{ap.streak ? `🔥 ${ap.streak} gün` : ""}</span></div>
            <Progress value={ap.progress} className="h-1.5 mt-1 bg-surface-alt [&>div]:bg-gold" />
          </div>
        )}
        {stats && <p className="text-xs text-ink-2 mt-3">Bu yıl okunan: <span className="font-medium">{stats.hedef_ilerleme} / {stats.yillik_hedef}</span> kitap</p>}
      </div>
      <nav className="border border-line rounded-xl bg-surface divide-y divide-line/60">
        {quickLinks.map((l) => (
          <Link key={l.to} to={l.to} data-testid={`side-link-${l.to.slice(1)}`} className="flex items-center gap-3 p-3 text-sm text-ink-2 hover:text-terra hover:bg-surface-alt/50 transition-colors">
            <l.icon size={16} className="text-terra/70" /> {l.label}
          </Link>
        ))}
      </nav>
      {dailyBook && (
        <Link to={`/kitap/${dailyBook.id}`} data-testid="daily-book"
          className="block border border-line rounded-xl p-4 bg-ink text-page hover:border-gold/50 transition-colors group">
          <div className="flex items-center gap-2 mb-2.5">
            <BookOpen size={14} className="text-gold" />
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold">Günün Kitabı</p>
          </div>
          <div className="flex gap-3 items-center">
            <div className="w-14 h-14 rounded-lg bg-page/10 border border-page/15 flex items-center justify-center overflow-hidden shrink-0">
              {dailyBook.kapak_resmi
                ? <img src={dailyBook.kapak_resmi} alt={dailyBook.baslik} className="w-full h-full object-cover" />
                : <BookOpen size={20} className="text-page/50" />}
            </div>
            <div className="min-w-0">
              <p className="font-serif text-base leading-snug group-hover:text-gold transition-colors line-clamp-2">{dailyBook.baslik}</p>
              <p className="text-xs text-page/60 truncate mt-0.5">{dailyBook.yazar}</p>
            </div>
          </div>
        </Link>
      )}
      {popTags.length > 0 && (
        <div className="border border-line rounded-xl p-4 bg-surface" data-testid="popular-tags">
          <div className="flex items-center gap-2 mb-3">
            <Tag size={15} className="text-terra" />
            <p className="text-sm font-medium text-ink">Popüler Etiketler</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {popTags.map((t) => (
              <Link key={t.etiket} to={`/kitaplar?etiket=${encodeURIComponent(t.etiket)}`}
                data-testid={`pop-tag-${t.etiket}`}
                className="text-xs text-terra bg-terra/8 border border-terra/15 px-2.5 py-1 rounded-full hover:bg-terra hover:text-white transition-colors">
                {t.etiket}
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
